/**
 * Schémas zod des payloads de l'API (§4.1 : « validation systématique »).
 * Partagés par le serveur (validation) et le front (typage des formulaires).
 */
import { z } from 'zod';
import { MAX_APP_DESCRIPTION_LENGTH, MAX_ARTIFACT_CODE_BYTES, MAX_COMMENT_LENGTH, MAX_MODIFICATION_INSTRUCTION_LENGTH, MAX_VERSION_COMMENT_LENGTH, MIN_APP_DESCRIPTION_LENGTH, } from '../constants.js';
import { validateSlug } from '../slug/validate-slug.js';
/** Slug d'application, validé par les mêmes règles que l'interface. */
export const slugSchema = z.string().superRefine((value, ctx) => {
    const result = validateSlug(value);
    if (!result.valid) {
        ctx.addIssue({ code: 'custom', message: result.message });
    }
});
/**
 * Nom de domaine. L'appartenance à l'allowlist `ALLOWED_DOMAINS` est vérifiée
 * côté serveur, où la configuration est connue.
 */
export const domainSchema = z
    .string({ message: 'Le domaine est obligatoire.' })
    .min(3, { message: 'Domaine invalide.' })
    .max(253, { message: 'Domaine invalide.' })
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, {
    message: 'Domaine invalide.',
});
/** Code d'artifact collé par l'utilisateur. */
export const artifactCodeSchema = z
    .string({ message: 'Le code de l’artifact est obligatoire.' })
    .min(1, { message: 'Le code de l’artifact est obligatoire.' })
    .refine((value) => new TextEncoder().encode(value).length <= MAX_ARTIFACT_CODE_BYTES, {
    message: 'Le code dépasse la taille maximale de 1 Mo.',
});
/** Commentaire libre associé à l'application. */
export const commentSchema = z.string().max(MAX_COMMENT_LENGTH).nullish();
/**
 * Description d'une application à créer de toutes pièces.
 *
 * L'auteur n'a pas toujours un artifact sous la main : décrire ce qu'il veut est
 * parfois le seul point de départ dont il dispose, et c'est déjà ainsi que
 * fonctionne la mise à jour d'une application existante.
 */
export const appDescriptionSchema = z
    .string({ message: 'Décrivez l’application souhaitée.' })
    .trim()
    .min(MIN_APP_DESCRIPTION_LENGTH, {
    message: 'Décrivez l’application en quelques phrases : ce qu’elle affiche, pour qui, et ce ' +
        'qu’on peut y faire. Trois mots ne suffisent pas — le modèle inventerait le reste.',
})
    .max(MAX_APP_DESCRIPTION_LENGTH, {
    message: `La description ne peut pas dépasser ${MAX_APP_DESCRIPTION_LENGTH} caractères.`,
});
/**
 * Création d'un déploiement, par code collé **ou** par description.
 *
 * Les deux champs sont facultatifs pris séparément, et exactement l'un des deux
 * est exigé. Un `code` obligatoire fermait la porte à la création par description ;
 * accepter les deux à la fois laisserait deux points de départ contradictoires,
 * sans rien pour les départager.
 */
export const createDeploymentSchema = z
    .object({
    code: artifactCodeSchema.optional(),
    description: appDescriptionSchema.optional(),
    slug: slugSchema,
    domain: domainSchema,
    comment: commentSchema,
    /**
     * Exiger une connexion à l'entreprise pour ouvrir l'application (§5.2).
     *
     * Décidé **à la création** et non modifiable ensuite : Cloudron n'ajoute pas
     * un addon à une application installée. Une application avec base de données
     * est protégée de toute façon.
     */
    forceSso: z.boolean().default(false),
    /** Case à cocher obligatoire du formulaire (§3.2), valeur tracée en audit. */
    confirmed: z.literal(true, {
        message: 'Vous devez confirmer l’absence de données confidentielles non autorisées.',
    }),
})
    .refine((value) => Boolean(value.code) !== Boolean(value.description), {
    message: 'Fournissez soit le code de l’artifact, soit une description de l’application — ' +
        'pas les deux, et pas aucun des deux.',
    path: ['code'],
});
/**
 * Commentaire de version. Contrairement au commentaire d'application, il est
 * **obligatoire** à chaque mise à jour (§3.5) : sans lui, un historique de
 * versions n'apprend rien à qui le consulte six mois plus tard.
 */
export const versionCommentSchema = z
    // Le message est porté par le type lui-même, et pas seulement par `min` :
    // sans cela, un champ absent produirait le message technique de zod
    // (« expected string, received undefined ») au lieu d'une phrase lisible.
    .string({ message: 'Décrivez en quelques mots ce que change cette version.' })
    .trim()
    .min(3, { message: 'Décrivez en quelques mots ce que change cette version.' })
    .max(MAX_VERSION_COMMENT_LENGTH, {
    message: `Le commentaire de version ne doit pas dépasser ${MAX_VERSION_COMMENT_LENGTH} caractères.`,
});
/** `POST /api/apps/:id/redeploy` — nouvelle version avec un nouveau code (§3.5). */
export const redeployAppSchema = z.object({
    code: artifactCodeSchema,
    versionComment: versionCommentSchema,
    comment: commentSchema,
    confirmed: z.literal(true, {
        message: 'Vous devez confirmer l’absence de données confidentielles non autorisées.',
    }),
});
/**
 * `POST /api/apps/:id/modify` — modification décrite en langage naturel (§3.5).
 *
 * Aucun code n'est transmis : la version en ligne fournit le point de départ, et
 * la demande dit ce qui doit changer. C'est le mode d'emploi de quelqu'un qui ne
 * lit pas le code — l'immense majorité des auteurs — et qui n'a de toute façon
 * plus l'artifact sous la main trois semaines après l'avoir déployé.
 */
export const modifyAppSchema = z.object({
    instruction: z
        .string({ message: 'Décrivez la modification souhaitée.' })
        .trim()
        .min(10, {
        message: 'Décrivez la modification en une phrase au moins : quelques mots ne suffisent pas à savoir quoi changer.',
    })
        .max(MAX_MODIFICATION_INSTRUCTION_LENGTH, {
        message: `La demande ne peut pas dépasser ${MAX_MODIFICATION_INSTRUCTION_LENGTH} caractères.`,
    }),
    versionComment: versionCommentSchema,
    comment: commentSchema,
    confirmed: z.literal(true, {
        message: 'Vous devez confirmer l’absence de données confidentielles non autorisées.',
    }),
});
/** `POST /api/apps/:id/rollback` — restauration d'une version antérieure (§3.5). */
export const rollbackSchema = z.object({
    versionId: z.string().min(1, { message: 'La version à restaurer est obligatoire.' }),
});
/**
 * `GET /api/apps/:id/versions/:vid/diff` — comparaison avec une autre version.
 * Sans `against`, l'API compare à la version qui précède immédiatement.
 */
export const versionDiffQuerySchema = z.object({
    against: z.string().min(1).optional(),
});
/**
 * `POST /api/deployments/:id/review` — décision sur les changements de
 * comportement annoncés par l'IA (§5.1).
 *
 * Le booléen est **exigé** et sans valeur par défaut : rien ne doit pouvoir
 * valider une mise en ligne par omission.
 */
export const reviewDecisionSchema = z.object({
    accept: z.boolean({ message: 'Indiquez si vous acceptez ces changements.' }),
});
/**
 * `POST /api/deployments/:id/refine` — précisions demandées à l'IA avant la mise
 * en ligne, en réponse aux changements de comportement annoncés (§5.1).
 *
 * Le seuil est plus bas que celui d'une description d'application : une précision
 * utile tient souvent en une phrase — « l'objectif de la jauge, c'est 15 » — alors
 * qu'une application entière ne se décrit pas en dix mots.
 */
export const refineDeploymentSchema = z.object({
    precisions: z
        .string({ message: 'Écrivez ce que Claude doit corriger.' })
        .trim()
        .min(10, {
        message: 'Dites en une phrase au moins ce qui doit changer : quelques mots ne suffisent pas.',
    })
        .max(MAX_MODIFICATION_INSTRUCTION_LENGTH, {
        message: `La précision ne peut pas dépasser ${MAX_MODIFICATION_INSTRUCTION_LENGTH} caractères.`,
    }),
});
/** `GET /api/apps/check-slug` — disponibilité d'un slug. */
export const checkSlugQuerySchema = z.object({
    slug: z.string(),
    domain: domainSchema,
});
/** `POST /api/apps/:id/delete-request` — demande de suppression. */
export const deleteRequestSchema = z.object({
    reason: z.string().max(500).nullish(),
});
/** Pagination commune aux listes. */
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
/** Identifiant de ressource dans un paramètre de route. */
export const idParamSchema = z.object({
    id: z.string().min(1),
});
//# sourceMappingURL=deployment.js.map